// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Loader2, 
  Trash2, 
  Image as ImageIcon,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '@/components/vendor-graphics/ImageUpload';

const VendorPortfolio: React.FC = () => {
  const { user } = useAuth();
  const [portfolioImages, setPortfolioImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [vendorProfile, setVendorProfile] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user?.id]);

  const fetchData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Get vendor profile with portfolio images
      const { data: profileData, error: profileError } = await (supabase as any)
        .from('graphics_vendor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      setVendorProfile(profileData);
      setPortfolioImages(profileData?.portfolio_images || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveImages = async () => {
    if (!vendorProfile?.id) return;

    try {
      setSaving(true);
      const { error } = await (supabase as any)
        .from('graphics_vendor_profiles')
        .update({ portfolio_images: portfolioImages })
        .eq('id', vendorProfile.id);

      if (error) throw error;
      toast.success('Portfolio updated successfully');
    } catch (error: any) {
      console.error('Error saving portfolio:', error);
      toast.error(error.message || 'Failed to save portfolio');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImage = async (imageUrl: string) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    const updatedImages = portfolioImages.filter(img => img !== imageUrl);
    setPortfolioImages(updatedImages);

    try {
      const { error } = await (supabase as any)
        .from('graphics_vendor_profiles')
        .update({ portfolio_images: updatedImages })
        .eq('id', vendorProfile.id);

      if (error) throw error;
      toast.success('Image deleted successfully');
    } catch (error: any) {
      console.error('Error deleting image:', error);
      toast.error(error.message || 'Failed to delete image');
      // Revert on error
      setPortfolioImages(portfolioImages);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Loading portfolio...</span>
      </div>
    );
  }

  if (!vendorProfile) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Set up your profile first</h3>
            <p className="text-muted-foreground text-center mb-4">
              You need to create your graphics vendor profile before you can manage your portfolio.
            </p>
            <Button onClick={() => window.location.href = '/vendor-graphics-dashboard/profile'}>
              Set up Profile
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Portfolio</h1>
          <p className="text-muted-foreground">
            Upload images of your best graphics work to showcase to potential clients
          </p>
        </div>
      </div>

      {/* Image Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Portfolio Images
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ImageUpload
            images={portfolioImages}
            onImagesChange={setPortfolioImages}
            maxImages={20}
          />
          <div className="flex justify-end">
            <Button 
              onClick={handleSaveImages} 
              disabled={saving}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Portfolio
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Portfolio Gallery */}
      {portfolioImages.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No portfolio images yet</h3>
            <p className="text-muted-foreground text-center">
              Upload images above to showcase your graphics design work
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Your Portfolio ({portfolioImages.length} images)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {portfolioImages.map((imageUrl, index) => (
                <div key={index} className="relative group aspect-square bg-muted rounded-lg overflow-hidden">
                  <img
                    src={imageUrl}
                    alt={`Portfolio image ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteImage(imageUrl)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VendorPortfolio;